import dayjs from "dayjs";
import { Formik } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdArrowBackIos,
  MdArrowForwardIos,
  MdOutlineCake,
  MdOutlineCalendarToday,
  MdOutlineGroups,
  MdOutlineLocationOn,
  MdOutlineMedicalServices,
  MdOutlineWorkOutline,
} from "react-icons/md";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT } from "utils/constants";
import {
  bindDateInputProps,
  bindInputProps,
  convertToTouched,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Section from "../Components/Section";
import { COLLECTIVE_REGIME_ORDER, EMPLOYEE_CATEGORY_ORDER } from "../constants";
import validation from "./validation";

const View = ({
  translation: T,
  initialValues,
  onSubmit,
  onBack,
  nextLoading,
  disabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const validationSchema = useMemo(
    () =>
      validation(t("VALIDATIONS", { returnObjects: true }), {
        firstOfMonthMessage: T.DEMOGRAPHICS.FORM.EFFECTIVE_DATE.FIRST_OF_MONTH,
      }),
    [t, T],
  );

  const categoryOptions = useMemo(
    () =>
      EMPLOYEE_CATEGORY_ORDER.map((value) => ({
        value,
        label: T.CATEGORIES[value],
      })),
    [T],
  );

  const regimeOptions = useMemo(
    () =>
      COLLECTIVE_REGIME_ORDER.map((value) => ({
        value,
        label: T.REGIMES[value],
      })),
    [T],
  );

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, setFieldValue, isValid, ...formProps }) => (
        <div className="ac-collectif contact-form">
          <div className="contact-form__content mb-16">
            <Section
              title={T.DEMOGRAPHICS.SECTIONS.SCOPE.TITLE}
              description={T.DEMOGRAPHICS.SECTIONS.SCOPE.DESCRIPTION}
            >
              <Select
                required
                disabled={disabled}
                options={categoryOptions}
                icon={<MdOutlineWorkOutline size={22} />}
                label={T.DEMOGRAPHICS.FORM.CATEGORY.LABEL}
                labelSuffix={T.DEMOGRAPHICS.FORM.CATEGORY.HINT}
                placeholder={T.DEMOGRAPHICS.FORM.CATEGORY.PLACEHOLDER}
                {...bindInputProps({
                  name: "demographics.category",
                  values,
                  ...formProps,
                })}
              />
              <Input
                required
                disabled={disabled}
                type="number"
                min={1}
                icon={<MdOutlineGroups size={22} />}
                label={T.DEMOGRAPHICS.FORM.HEADCOUNT.LABEL}
                placeholder={T.DEMOGRAPHICS.FORM.HEADCOUNT.PLACEHOLDER}
                {...bindInputProps({
                  name: "demographics.headcount",
                  values,
                  ...formProps,
                })}
              />
              <Input
                required
                disabled={disabled}
                type="number"
                icon={<MdOutlineCake size={22} />}
                label={T.DEMOGRAPHICS.FORM.AVERAGE_AGE.LABEL}
                placeholder={T.DEMOGRAPHICS.FORM.AVERAGE_AGE.PLACEHOLDER}
                {...bindInputProps({
                  name: "demographics.averageAge",
                  values,
                  ...formProps,
                })}
              />
            </Section>

            <Section
              title={T.DEMOGRAPHICS.SECTIONS.RATING.TITLE}
              description={T.DEMOGRAPHICS.SECTIONS.RATING.DESCRIPTION}
            >
              <Select
                required
                disabled={disabled}
                options={regimeOptions}
                icon={<MdOutlineMedicalServices size={22} />}
                label={T.DEMOGRAPHICS.FORM.REGIME.LABEL}
                placeholder={T.DEMOGRAPHICS.FORM.REGIME.PLACEHOLDER}
                {...bindInputProps({
                  name: "demographics.regime",
                  values,
                  ...formProps,
                })}
              />
              <Input
                disabled
                icon={<MdOutlineLocationOn size={22} />}
                label={T.DEMOGRAPHICS.FORM.DEPARTMENT.LABEL}
                labelSuffix={T.DEMOGRAPHICS.FORM.DEPARTMENT.HINT}
                placeholder={T.DEMOGRAPHICS.FORM.DEPARTMENT.EMPTY}
                {...bindInputProps({
                  name: "demographics.department",
                  values,
                  ...formProps,
                })}
              />
              <Input
                required
                disabled={disabled}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                defaultPickerValue={dayjs().add(1, "month").startOf("month")}
                disabledDate={(current) => current && current.date() !== 1}
                icon={<MdOutlineCalendarToday size={22} />}
                label={T.DEMOGRAPHICS.FORM.EFFECTIVE_DATE.LABEL}
                placeholder={T.DEMOGRAPHICS.FORM.EFFECTIVE_DATE.PLACEHOLDER}
                {...bindDateInputProps({
                  name: "demographics.effectiveDate",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
              />
            </Section>
          </div>
          <ActionButtons
            onBack={onBack}
            onSubmit={async () => {
              await formProps.setTouched(
                convertToTouched(formProps.errors),
                true,
              );
              if (isValid) handleSubmit();
            }}
            loading={nextLoading}
            disabled={{ next: nextLoading || disabled }}
            icons={{
              next: <MdArrowForwardIos size={16} />,
              previous: <MdArrowBackIos size={16} />,
            }}
            translation={T.ACTIONS}
          />
        </div>
      )}
    </Formik>
  );
};

export default View;
